import CustomTableWithPagination from "../../../reusable-components/CustomTable";
import {
  buttonParams,
  generateBatchFailure,
  getApprovedTicketColumns,
  getApprovedTicketModalParams,
  textConsts,
} from "./constant";
import styles from "./ApprovedTickets.module.scss";
import { Modal, Button, message } from "antd";
import { useEffect, useState } from "react";
import { ButtonType } from "../../../enums/ButtonAttributesEnums";
import { useLocation } from "react-router-dom";
import TicketProcessingServices from "../../../services/ticketProcessing.service";
import { useTicketProcessingFilterContext } from "../../../context/TicketProcessingFilterContext";
import TNLoader from "../../../reusable-components/TNLoader";
import GenerateSuccessModal from "../GenerateSuccessModal";
import { useQueryClient } from "@tanstack/react-query";
import { QueryKeys } from "../../../constants/QueryKeys";

const ApprovedTickets = ({
  approveCount,
  hasInvoiceAccess = false,
}: {
  approveCount: number;
  hasInvoiceAccess: boolean;
}) => {
  const columns = getApprovedTicketColumns();
  const queryClient = useQueryClient();
  const { processingFilters } = useTicketProcessingFilterContext();
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const handleCurrentPage = (page: number) => {
    setCurrentPage(page);
  };
  const handlePagination = (page: number) => handleCurrentPage(page);
  const handlePaginationPageSize = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const location = useLocation();
  const [startDate, endDate] = location?.pathname?.split("/")?.[3]?.split("_")?.map(decodeURIComponent);
  const [ticketCount, setTicketCount] = useState(0);
  const [isConfirmGenerateInvoiceModalOpen, setConfirmGenerateInvoiceModalOpen] = useState(false);
  const [invoiceGenerationSuccessOpen, setInvoiceGenerationSuccessOpen] = useState(false);
  const { FetchApprovedTickets, GenerateInvoices } = TicketProcessingServices();
  const {
    data: approvedTicketsData,
    refetch: refetchApprovedTickets,
    isPending: isApprovedTicketsDataPending,
  } = FetchApprovedTickets({
    companyId: processingFilters?.customerName,
    jobId: processingFilters?.jobName,
    truckId: processingFilters?.truckNumber,
    startDate: startDate,
    endDate: endDate,
    page: currentPage,
    perPage: pageSize,
  });
  const { mutateAsync: generateInvoices, isPending: generateInvoicesPending } = GenerateInvoices();
  const data = approvedTicketsData?.data ?? [];

  const handleConfirmGenerateInvoiceOpen = () => {
    setConfirmGenerateInvoiceModalOpen(true);
  };
  const handleConfirmGenerateInvoiceClose = () => {
    setConfirmGenerateInvoiceModalOpen(false);
  };
  const handleSuccessOpen = () => {
    setInvoiceGenerationSuccessOpen(true);
  };
  const handleSuccessClose = () => {
    setInvoiceGenerationSuccessOpen(false);
  };

  const handleInvoiceGeneration = async () => {
    handleConfirmGenerateInvoiceClose();
    const payloadArray = [
      {
        startDate: startDate,
        endDate: endDate,
      }
    ];
    await generateInvoices(payloadArray, {
      onSuccess: (data) => {
        const invoicesGenerated = data?.summary?.invoiceCount;
        const partialGenerationTrue = data?.summary?.partialInvoice ?? false;
        setTicketCount(invoicesGenerated)
        if(!!invoicesGenerated) {
          if(partialGenerationTrue)
            message.warning({
              content: textConsts.PARTIAL_BATCH_FAILURE,
              duration: 5,
            })
          handleSuccessOpen();
        } else {
          message.error({
            content: textConsts.TOTAL_BATCH_FAILURE,
            duration: 5,
          });
        }
        queryClient.invalidateQueries([QueryKeys.FETCH_APPROVED_TICKETS_KEY, QueryKeys.FETCH_TICKET_PROCESSING_SECTION_COUNT_KEY]);
      },
      onError: (error) => {
        message.warn({
          content: error?.response?.data?.message ?? textConsts.SOMETHING_WENT_WRONG,
          duration: 5,
        })
      },
    })
  }

  const approvedTicketModalParams = getApprovedTicketModalParams(
    approveCount ?? 0
  );
  useEffect(() => {
    refetchApprovedTickets();
  }, [currentPage, pageSize]);
  return (
    <>
      <TNLoader 
        isLoading={generateInvoicesPending}
      />
      <GenerateSuccessModal
        isOpen={invoiceGenerationSuccessOpen}
        handleClose={handleSuccessClose}
        ticketCount={ticketCount}
        hasInvoiceAccess={hasInvoiceAccess}
      />
      <Modal
        visible={isConfirmGenerateInvoiceModalOpen}
        okText={approvedTicketModalParams.okButtonLabel}
        onOk={handleInvoiceGeneration}
        onCancel={handleConfirmGenerateInvoiceClose}
        title={approvedTicketModalParams.title}
      >
        {approvedTicketModalParams.confirmGenerateInvoice}
      </Modal>
      <Button
        onClick={handleConfirmGenerateInvoiceOpen}
        className={styles.InvoiceButton}
        type={ButtonType.PRIMARY}
      >
        {buttonParams.title}
      </Button>
      <CustomTableWithPagination
        columns={columns}
        dataSource={data}
        loading={isApprovedTicketsDataPending}
        handleSizeChangerChange={handlePaginationPageSize}
        handleChange={handlePagination}
        totalData={approvedTicketsData?.meta?.totalRecords}
      />
    </>
  );
};

export default ApprovedTickets;
